import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, Tooltip as ReTooltip, ResponsiveContainer, Cell } from "recharts";
import { BarChart3, ListOrdered, Wrench, ChevronRight, Download } from "lucide-react";
import { useMaintenance } from "@/components/MaintenanceLayout";
import { MachineMark } from "@/components/maintenance/MachineMark";
import MachineRatesPanel from "@/components/maintenance/MachineRatesPanel";
import MaintenanceAlertsPanel from "@/components/maintenance/MaintenanceAlertsPanel";
import MaintenanceActivityCard from "@/components/maintenance/MaintenanceActivityCard";
import MachineRequestsPanel from "@/components/maintenance/MachineRequestDialogs";
import {
  MAINT_STAGES, humanDuration, humanDurationLive, formatINR, clockedSeconds,
} from "@/components/maintenance/lib";

// The landing view on every log-on — the ERP main-dashboard anatomy, adapted.
// One hero glass slab (headline stats + the PIPELINE bucket cards that carry
// the per-stage counts), then Live Activity | FY chart, then the two ranked
// panels. NO month filters anywhere: a machine can be down for more than a
// month and must stay visible (MAINTENANCE_MODULE_PLAN.md §2.8, §8b).

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REPORTED_FILL = "#6d94c4";
const REPAIRED_FILL = "#22c55e";

function Spark({ points, color }) {
  const pts = Array.isArray(points) && points.length ? points : [0, 0, 0, 0, 0, 0, 0];
  const max = Math.max(1, ...pts);
  const step = 50 / Math.max(1, pts.length - 1);
  const d = pts.map((v, i) => `${(1 + i * step).toFixed(1)},${(17 - (v / max) * 14).toFixed(1)}`).join(" ");
  return (
    <svg className="absolute right-3.5 bottom-3.5" width="52" height="20" viewBox="0 0 52 20" fill="none" aria-hidden="true">
      <polyline points={d} stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" opacity="0.75" />
    </svg>
  );
}

function HeroStat({ label, dot, children, last }) {
  return (
    <>
      <div className="flex flex-col gap-0.5 pb-3">
        <div className="flex items-center gap-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[hsl(var(--muted-foreground))]/70 mb-[5px]">
          {dot && <span className="w-[7px] h-[7px] rounded-full" style={{ background: dot }} />}
          {label}
        </div>
        {children}
      </div>
      {!last && <div className="w-px h-9 bg-[hsl(var(--border))]/70 mx-7 mt-4 shrink-0" aria-hidden="true" />}
    </>
  );
}

const HeroNum = ({ children, className = "", style }) => (
  <div className={`font-mono-data font-bold text-[1.5rem] leading-none tracking-[-0.025em] ${className}`} style={style}>{children}</div>
);

function Panel({ title, icon: Icon, right, children, testid, className = "" }) {
  return (
    <div className={`rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] px-[18px] py-4 ${className}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.08em] text-[hsl(var(--foreground))]/85 mb-3.5">
        {Icon && <Icon className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />}
        {title}
        {right}
      </div>
      {children}
    </div>
  );
}

function RankRows({ rows, emptyText, renderName }) {
  if (!rows.length) {
    return <p className="text-[12px] text-[hsl(var(--muted-foreground))] py-4 leading-relaxed">{emptyText}</p>;
  }
  const max = Math.max(...rows.map((r) => r.count), 1);
  return rows.map((r, i) => (
    <div key={r.key} className="flex items-center gap-3 py-[7px]">
      <span className="font-mono-data text-[11px] font-extrabold text-[hsl(var(--muted-foreground))]/70 w-5">{String(i + 1).padStart(2, "0")}</span>
      <div className="flex-1 min-w-0">
        {renderName(r)}
        <div className="rl-rank-track"><div className="rl-rank-fill" style={{ width: `${(r.count / max) * 100}%` }} /></div>
      </div>
      <span className="font-mono-data text-[13px] font-extrabold ml-auto">{r.count}</span>
    </div>
  ));
}

function FyTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  const p = Object.fromEntries(payload.map((x) => [x.dataKey, x.value]));
  return (
    <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 rounded-lg px-3 py-2 shadow-md text-xs" style={{ fontFamily: "'Onest', sans-serif" }}>
      <p className="font-bold text-zinc-800 dark:text-zinc-100 mb-1.5">{label}</p>
      <div className="flex items-center gap-2 text-zinc-600 dark:text-zinc-300">
        <span className="w-2 h-2 rounded-sm" style={{ background: REPORTED_FILL }} />
        Reported: <b className="text-zinc-800 dark:text-zinc-100">{p.reported ?? 0}</b>
      </div>
      <div className="flex items-center gap-2 text-zinc-600 dark:text-zinc-300">
        <span className="w-2 h-2 rounded-sm" style={{ background: REPAIRED_FILL }} />
        Repaired &amp; confirmed: <b className="text-zinc-800 dark:text-zinc-100">{p.repaired ?? 0}</b>
      </div>
    </div>
  );
}

export default function MaintenanceDashboard() {
  const navigate = useNavigate();
  const { refreshKey, can = {}, counts, refresh } = useMaintenance() || {};
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [error, setError] = useState("");
  const [nowMs, setNowMs] = useState(() => Date.now());

  // Once a second: the money and the longest-open clock are both still moving.
  useEffect(() => {
    const tick = () => { if (!document.hidden) setNowMs(Date.now()); };
    const t = setInterval(tick, 1000);
    const onVis = () => tick();
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVis); };
  }, []);

  const load = useCallback(async () => {
    try {
      const [s, a] = await Promise.all([
        axios.get(`${API}/maintenance/summary`),
        axios.get(`${API}/maintenance/activity?limit=60`),
      ]);
      setSummary(s.data); setActivity(a.data?.activity || []); setError("");
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not load the Maintenance dashboard.");
    }
  }, []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const [reportBusy, setReportBusy] = useState(false);
  const downloadMonthly = useCallback(async () => {
    setReportBusy(true);
    try {
      const r = await axios.get(`${API}/maintenance/report/monthly`, { responseType: "blob" });
      // Content-Disposition isn't readable cross-origin, so name the file the
      // same way the server does — last month, YYYY-MM.
      const d = new Date();
      d.setDate(1); d.setMonth(d.getMonth() - 1);
      const name = `RockLink-Maintenance-${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}.pdf`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Could not build the monthly report.");
    } finally { setReportBusy(false); }
  }, []);

  const fyData = useMemo(() => (summary?.fy_months || []).map((m, i) => ({
    ...m, isCurrent: i === summary?.fy_now_index,
  })), [summary]);

  // The FY cost as of THIS second: what the server computed, plus the accrual
  // of every open stopped machine that has a ₹/hr rate. Real money, not an
  // animation — with nothing stopped the number simply sits still.
  const liveCost = useMemo(() => {
    if (!summary?.cost_known) return null;
    const base = summary.downtime_cost_fy || 0;
    const rate = summary.cost_rate_inr_per_hour || 0;
    if (!rate) return base;
    const elapsed = Math.max(0, (nowMs - Date.parse(summary.as_of || 0)) / 1000);
    return base + (rate * elapsed) / 3600;
  }, [summary, nowMs]);

  const closureRate = summary?.fy_reported
    ? Math.round((summary.fy_closed / summary.fy_reported) * 100)
    : null;

  if (error) {
    return <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] p-8 text-center text-[13px] text-[hsl(var(--muted-foreground))]">{error}</div>;
  }
  if (!summary) {
    return <div className="rl-glass-edge rounded-[14px] bg-[hsl(var(--card))] p-8 text-center text-[13px] text-[hsl(var(--muted-foreground))]">Loading…</div>;
  }

  const b = summary.buckets || {};
  const longest = summary.longest_open;
  const bucketCopy = {
    received: {
      count: b.received?.count ?? 0, unit: (b.received?.count === 1 ? "machine" : "machines"),
      sub: (b.received?.count ?? 0) === 0
        ? <>nothing waiting</>
        : <><b className="font-[650] text-[hsl(var(--foreground))]/80">{b.received?.stopped ?? 0} stopped</b>{b.received?.degraded ? ` · ${b.received.degraded} running with problem` : ""}</>,
    },
    repair: {
      count: b.repair?.count ?? 0, unit: (b.repair?.count === 1 ? "machine" : "machines"),
      sub: (b.repair?.count ?? 0) === 0
        ? <>nobody is mid-repair</>
        : <>{b.repair?.waiting_spares ? `${b.repair.waiting_spares} waiting for spares · ` : ""}longest <b className="font-[650] rl-tx-red">{humanDuration(b.repair?.longest_seconds)}</b></>,
    },
    handover: {
      count: b.handover?.count ?? 0, unit: (b.handover?.count === 1 ? "machine" : "machines"),
      sub: <>waiting for production sign-off</>,
    },
    complete: {
      count: b.complete?.count ?? 0, unit: "this FY",
      sub: b.complete?.avg_total_seconds
        ? <>avg total downtime <b className="font-[650] text-[hsl(var(--foreground))]/80">{humanDuration(b.complete.avg_total_seconds)}</b></>
        : <>nothing closed yet this year</>,
    },
  };

  return (
    <div data-testid="maintenance-dashboard">
      {/* ── Hero slab ─────────────────────────────────────────────────────── */}
      <div className="rl-glass rl-hero-glass rounded-[18px] px-6 pt-5 pb-[22px] mb-4">
        <div className="flex items-start flex-wrap">
          <HeroStat label="Machines"><HeroNum>{summary.machines_total}</HeroNum></HeroStat>
          <HeroStat label="Running" dot="#22c55e"><HeroNum>{summary.machines_running}</HeroNum></HeroStat>
          <HeroStat label={`Downtime cost · ${summary.fy_label}`}>
            {summary.cost_known
              ? (
                <div className="flex items-baseline gap-2 flex-wrap">
                  <HeroNum className={summary.cost_rate_inr_per_hour ? "rl-tx-red" : ""}>{formatINR(liveCost)}</HeroNum>
                  {summary.cost_rate_inr_per_hour > 0 && (
                    <span className="text-[11px] rl-tx-red" title="Open stopped machines are still accruing at this rate">
                      +{formatINR(summary.cost_rate_inr_per_hour)}/hr right now
                    </span>
                  )}
                </div>
              )
              : (
                <div className="flex items-baseline gap-2">
                  <HeroNum className="text-[hsl(var(--muted-foreground))]">₹ —</HeroNum>
                  <span className="text-[11px] text-[hsl(var(--muted-foreground))]">set machine rates to see cost</span>
                </div>
              )}
          </HeroStat>
          <HeroStat label="Avg repair time">
            <HeroNum className={summary.avg_repair_seconds == null ? "text-[hsl(var(--muted-foreground))]" : ""}>
              {summary.avg_repair_seconds == null ? "—" : humanDuration(summary.avg_repair_seconds)}
            </HeroNum>
          </HeroStat>
          <HeroStat label="Longest open" dot="#f59e0b" last>
            {longest ? (
              <div className="flex items-baseline gap-2 flex-wrap">
                <HeroNum className="rl-tx-amber" style={{ fontSize: 24 }}>
                  {longest.clock_start_at
                    ? humanDurationLive(clockedSeconds(longest, nowMs))
                    : humanDuration(longest.seconds)}
                </HeroNum>
                <span className="text-[12px] font-semibold text-[hsl(var(--muted-foreground))]">{longest.machine}</span>
              </div>
            ) : (
              <div className="flex items-baseline gap-2">
                <HeroNum className="rl-tx-green">None</HeroNum>
                <span className="text-[12px] text-[hsl(var(--muted-foreground))]">every machine is running</span>
              </div>
            )}
          </HeroStat>
        </div>

        <div className="text-[10px] font-bold uppercase tracking-[0.12em] text-[hsl(var(--muted-foreground))] mt-1 mb-2.5">Pipeline</div>
        <div className="grid grid-cols-2 min-[900px]:grid-cols-4 gap-3.5">
          {MAINT_STAGES.map((s) => {
            const c = bucketCopy[s.key];
            return (
              <button
                key={s.key}
                onClick={() => navigate(s.path)}
                className="rl-bucket"
                style={{ "--bc": s.color }}
                data-testid={`mt-bucket-${s.key}`}
              >
                <div className="flex items-center justify-between text-[12.5px] font-semibold" style={{ fontFamily: "var(--rl-font-heading), sans-serif", color: s.color }}>
                  {s.label}
                  <ChevronRight className="w-3.5 h-3.5 text-[hsl(var(--muted-foreground))]/60" />
                </div>
                <div className="font-mono-data font-bold text-[1.5rem] tracking-[-0.025em] mt-[9px] leading-none text-left">
                  {c.count}
                  <small className="text-[11px] font-medium text-[hsl(var(--muted-foreground))] tracking-normal ml-1" style={{ fontFamily: "'Onest', sans-serif" }}>{c.unit}</small>
                </div>
                <div className="text-[11px] text-[hsl(var(--muted-foreground))] mt-1.5 text-left">{c.sub}</div>
                <Spark points={b[s.key]?.spark} color={s.color} />
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Live Activity | FY chart ───────────────────────────────────────── */}
      {/* The feed is a fixed-height, self-scrolling card (the ERP's Live
          Activity anatomy) — it can never grow the page again — and it gets
          real width, because three-line rows need it. */}
      <div className="grid gap-4 items-stretch lg:[grid-template-columns:440px_1fr]">
        <MaintenanceActivityCard activity={activity} className="h-[420px]" />

        <Panel title={<>Breakdowns vs Repaired · <span className="text-[hsl(var(--primary))]">{summary.fy_label}</span></>} icon={BarChart3} testid="mt-fy-chart"
               className="flex flex-col min-h-0">
          <div className="flex-1 min-h-[190px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={fyData} margin={{ top: 8, right: 4, left: 4, bottom: 0 }} barGap={2}>
                <XAxis
                  dataKey="label" axisLine={false} tickLine={false}
                  tick={{ fontSize: 9, fontFamily: "var(--rl-font-data), sans-serif", fill: "hsl(var(--muted-foreground))" }}
                />
                <ReTooltip cursor={{ fill: "hsl(var(--muted) / 0.35)" }} content={<FyTooltip />} />
                <Bar dataKey="reported" radius={[4, 4, 0, 0]} barSize={12}>
                  {fyData.map((m, i) => <Cell key={i} fill={m.reported ? REPORTED_FILL : "hsl(var(--muted))"} />)}
                </Bar>
                <Bar dataKey="repaired" radius={[4, 4, 0, 0]} barSize={12}>
                  {fyData.map((m, i) => <Cell key={i} fill={m.repaired ? REPAIRED_FILL : "hsl(var(--muted))"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="flex gap-4 flex-wrap items-center mt-3 text-[11px] text-[hsl(var(--muted-foreground))]">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: REPORTED_FILL }} />Reported</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-[3px]" style={{ background: REPAIRED_FILL }} />Repaired &amp; confirmed</span>
            <span className="ml-auto">
              {summary.fy_reported} reported · {summary.fy_closed} closed
              {closureRate !== null && <> · <b className="rl-tx-green">{closureRate}% closed</b></>}
            </span>
          </div>
        </Panel>
      </div>

      {/* ── Ranked panels ─────────────────────────────────────────────────── */}
      <div className="grid gap-4 mt-4 min-[900px]:grid-cols-2">
        <Panel title="Most common problems" icon={ListOrdered} testid="mt-top-reasons">
          <RankRows
            rows={(summary.top_reasons || []).map((r) => ({ key: r.reason, count: r.count, reason: r.reason }))}
            emptyText="Once a few breakdowns are closed, the problems that keep coming back rank here."
            renderName={(r) => <div className="text-[12.5px] font-semibold">{r.reason}</div>}
          />
        </Panel>
        <Panel title="Top breakdown machines" icon={Wrench} testid="mt-top-machines">
          <RankRows
            rows={(summary.top_machines || []).map((m) => ({ key: m.machine_no, count: m.count, ...m }))}
            emptyText="The machines that break down most will surface here as reports come in."
            renderName={(m) => (
              <div className="flex items-center gap-2 min-w-0">
                <MachineMark make={m.make} size={20} />
                <span className="text-[12.5px] font-semibold truncate">{m.name}</span>
                <span className="rl-me-chip rl-carved shrink-0">{m.machine_no}</span>
              </div>
            )}
          />
        </Panel>
      </div>

      {/* Admin corner — per-machine ₹/hr, collapsed by default so it never
          competes with the daily read. */}
      {/* Admin corner. The monthly report sits with it — a quiet download of
          last month's page, the same one the chairman is emailed on the 1st. */}
      {can.can_admin_maintenance && (
        <div className="flex justify-end mt-4">
          <button
            onClick={downloadMonthly}
            disabled={reportBusy}
            title="Last month's maintenance report, as a PDF"
            data-testid="mt-monthly-report"
            className="rl-pill inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[12px] font-semibold disabled:opacity-50"
          >
            <Download className="w-3.5 h-3.5" /> {reportBusy ? "Preparing…" : "Monthly report"}
          </button>
        </div>
      )}
      {can.can_admin_maintenance && (
        <MachineRequestsPanel pendingCount={counts?.machine_requests_pending || 0} onChanged={refresh} />
      )}
      {can.can_admin_maintenance && <MachineRatesPanel />}
      {can.can_admin_maintenance && <MaintenanceAlertsPanel />}
    </div>
  );
}
